<script type="text/jsx" lang="jsx">
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { BILLING_CYCLE, planTypes, BASE_PLAN_TYPES, DEFAULT_FREE_TRIAL_DAYS, BillingCycleEnum } from '@/common/constants';
import { routePathMap } from '@/router/constant';
import { get, toUpper } from 'lodash';
import { dateTimeFormatWithUtcOffset } from '@/utils/dateformat';
import { getDefaultPayApmInfo } from "@/store/modules/payment";
import { plansof } from '@/utils/chain';
import { pageConfig } from '@/common/service/page-config';
import { PLAN_TYPES_WITH_MULTI_BASE_PLANS } from '@/plans/ROOM_CONNECTOR/common/utils';
import { billingCycleToValueMap } from '@/utils';
import { getMultipleRatePlanSumPrice } from '@/store/modules/shopping-cart';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { isFromShoppingCart, isProductCanRemove } from '@/views/config/shopping-cart/persist/plan-remove';
import NextChargeDate from '@/views/config/shopping-cart/next-charge-date.vue';
import PlanDiscountList from '@/views/config/shopping-cart/discount/plan-discount-list.vue';
import { getEffectiveAmount } from '@/utils/downsell-price';
import NextRenewalDate from '@/views/config/shopping-cart/next-renewal-date.vue';
import { isFromCampaignLink } from '@/views/config/coupon/helper/utils';

// render priority list
export const renderKeys = ['renderPrice', 'renderTip', 'renderAgreement', 'renderSuccessTitle'];

// render controls
export const props = renderKeys.reduce((acc, x) => {
  acc[x] = {
    type: Boolean,
    default: false,
  };
  return acc;
}, {});

export const CART_DISPLAY_MODE = {
  BOUGHT: 'bought', // only bought
  DELTA: 'delta', // only delta added
  SUM: 'sum', // both bought and added
};

export default {
  components: {
    PlanDiscountList,
    NextRenewalDate,
  },
  props: {
    ...props,
    $pkg: Object, // plan config package of @/plans
    mode: {
      type: String,
      default: CART_DISPLAY_MODE.BOUGHT,
    },
    filterKeys: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    const $context = this.$context;
    if (!$context.is.checkoutPage) return;
    const eventType = 'plan-edit-action';
    $context.$off(eventType).$on(eventType, () => this.ma_edit_action());
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'discountWithAmountMap', 'boughtPlans', 'apmInfo', 'partnerRefer', 'eligiblePartnerReferOffer', 'migratePlanState', 'couponConfiguration']),
    mx_plans() {
      const key = this.$options.key;
      const type = planTypes[key];
      return this.getSelectedPlans.filter((x) => toUpper(x.type) === toUpper(type));
    },
    mx_startDate() {
      // TODO consider paid2paid date
      return new Date();
    },
    mx_isLastBasePlan() {
      // is this the last base plan that should not be removed
      const key = this.$options.key;
      const type = planTypes[key];
      if (!BASE_PLAN_TYPES.includes(type)) return false; // is not base plan
      let baseTypes = this.mx_plans.map((x) => x.type).filter((x) => BASE_PLAN_TYPES.includes(x)); // base plans among these
      if (baseTypes.length === 1 && baseTypes[0] === type) return true; // only when

      return false;
    },
    // For CRC special case that need to check the last same billing cycle base plan since it could be more than 1 base plan
    mx_isLastBasePlanForTheSameBillingCycle() {
      // Some plan may has more than 1 base plan dependencies like CRC
      const hasThePlanNeedsMultiBasePlan = plansof(this.getSelectedPlans).filter((plan) => PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(plan.type)).has();
      const currentPrimaryPlan = plansof(this.getSelectedPlans).filter((plan) => PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(plan.type)).get(0);
      // Get current mixin base plan type like zoom_meeting zoomroom
      const key = this.$options.key;
      const type = planTypes[key];
      // e.g CRC as the primary plan
      if(hasThePlanNeedsMultiBasePlan) {
        // CRC billing cycle
        const currentPrimaryPlanBillingCycle = currentPrimaryPlan.billing_cycle;
        // Filter base plans which are the same billing cycle or annual > monthly with CRC
        const SameBillingCycleBasePlans = plansof(this.getSelectedPlans).type(...BASE_PLAN_TYPES).filter(basePlan => BillingCycleEnum[basePlan.billing_cycle] >= BillingCycleEnum[currentPrimaryPlanBillingCycle]);
        // Find the last billing cycle
        const theLastSameBillingCycleBasePlanType = SameBillingCycleBasePlans.get(-1).type;
        // is this plan now
        return theLastSameBillingCycleBasePlanType === type
      }

      return false;
    },
  },
  methods: {
    ...mapMutations(['setApmInfo']),
    ...mapActions(['checkProMonthlyAndYearlyPriceChange']),
    _dispatch(type, ...args) {
      const planType = get(args, '0.type', '');
      let key = this.$options.key;
      if(planType && planTypes[planType]) {
        key = planTypes[planType];
      }
      this.$emit(type, key, ...args);
    },
    mx_main_title(title, value, parentCategory) {
      return <h3 class="opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__">
        <span data-parent-category-title={parentCategory}>{ title }</span>
        {value !== undefined && <span class="opc-cart-item__value text-no-capitalize">{value}</span>}
      </h3>
    },
    mx_title(title, price, planCode, parentCategory, classList) {
      let _price = typeof price === 'number' ? this.$money(price) : price;
      return (
        <h3 class={'opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__' + (classList ? ' ' + classList.join(' ') : '')}>
          <span className="opc-cart-item__subtitle"
            data-parent-category-title={parentCategory}
            data-plan-code-title={planCode}>{title}</span>
          {price !== undefined && <span class="opc-cart-item__value text-no-capitalize">{_price}</span>}
        </h3>
      );
    },
    mx_price(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && bp.plan_code === plan.plan_code);
      if (boughtPlan?.ratePlans?.length > 1) {
        return getMultipleRatePlanSumPrice(boughtPlan, plan.cartInfo.selected_number);
      }

      // Create context for downsell logic - try to get current license from bought plan
      const context = {
        currentLicense: boughtPlan?.cartInfo?.selected_number ||
                       this.$context?.currentEditPlan?.cartInfo?.selected_number ||
                       0
      };

      // Get effective amount considering downsell logic
      const effectiveAmount = getEffectiveAmount(plan, context);

      if (plan.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY) {
        return effectiveAmount / billingCycleToValueMap[plan.billing_cycle] * plan.cartInfo.selected_number;
      }

      return plan.cartInfo.selected_number * effectiveAmount;
    },
    mx_price_bought(plan){
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && bp.plan_code === plan.plan_code);
      return this.mx_price(boughtPlan);
    },
    mx_price_delta(plan){
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return this.mx_price(plan) - this.mx_price(boughtPlan);
    },
    mx_price$(plan){
      if(this.mode === CART_DISPLAY_MODE.DELTA) return this.mx_price_delta(plan);
      if(this.mode === CART_DISPLAY_MODE.BOUGHT) return this.mx_price_bought(plan);
      return this.mx_price(plan);
    },
    mx_subTitle(title, price, planCode) {
      let _price = typeof price === 'number' ? this.$money(price) : price;
      return (
        <h3 class="opc-cart-item__sub-title __PLACEHOLDER_sub_title__">
          <span data-plan-code-title={planCode}>{title}</span>
          {price !== undefined && <span class="opc-cart-item__value text-no-capitalize">{_price}</span>}
        </h3>
      );
    },
    // FREE TRIAL - shopping cart title - show Title + 'Free Trial' as free trial plan = paid plan + 0 price
    mx_freeTrialTitle(title, planCode, parentCategory) {
      return (
        <h3 class="opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__">
          <span data-plan-code-title={planCode} data-parent-category-title={parentCategory}>{this.$t('billing.trial.title', [title])}</span>
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(0)}</span>
        </h3>
      );
    },
    mx_freeTrialStartDate(startTime = new Date()) {
      // show pst data time
      return this.$t('billing.trial.start_date', [dateTimeFormatWithUtcOffset(startTime, {offset: -8})]);
    },
    mx_freeTrialDesc(trialDays = DEFAULT_FREE_TRIAL_DAYS) {
      return (
        <div class="opc-cart-item__text">
          <p class="opc-cart-item__duration-text">{this.$t('billing.trial.duration_desc_cart', [trialDays])}</p>
          <p>{this.mx_freeTrialStartDate()}</p>
        </div>
      );
    },
    mx_license(num, unit = this.$t('billing.planform.unit.user')) {
      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    mx_license_bought(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return boughtPlan?.cartInfo?.selected_number;
    },
    mx_license_delta(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return plan.cartInfo.selected_number - boughtPlan?.cartInfo?.selected_number;
    },
    mx_license$(plan){
      let num;
      if(this.mode === CART_DISPLAY_MODE.DELTA) {
        num = this.mx_license_delta(plan);
      }else if(this.mode === CART_DISPLAY_MODE.BOUGHT){
        num = this.mx_license_bought(plan);
      }else{
        num = plan?.cartInfo?.selected_number;
      };
      return this.mx_license(num);
    },
    mx_renewal_date() {
      return [
        <next-renewal-date />,
      ];
    },
    mx_cycle(cycle, remark = '') {
      const labels = {
        [BILLING_CYCLE.MONTHLY]: this.$t('billing.order_summary.monthly_subscription'),
        [BILLING_CYCLE.ANNUAL]: this.$t('billing.order_summary.annual_subscription'),
        [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: this.$t('billing.order_summary.annual_term_monthly_subscription'),
        [BILLING_CYCLE.ONE_MONTH]: this.$t('billing.billing_cycle.one_month_new'),
        [BILLING_CYCLE.QUARTERLY]: this.$t('billing.order_summary.quarterly_subscription'),
        [BILLING_CYCLE.TWO_YEARS]: this.$t('billing.order_summary.two_years_subscription'),
        [BILLING_CYCLE.THREE_YEARS]: this.$t('billing.order_summary.three_years_subscription'),
        [BILLING_CYCLE.PAY_AS_YOU_GO]: this.$t('billing.pay_you_go'),
        [BILLING_CYCLE.PAY_PER_ATTENDEE]: this.$t('billing.billing_cycle.pay_per_attendee'),
        [BILLING_CYCLE.ONE_TIME]: this.$t('billing.billing_cycle.one_time'),
      };
      if (!labels[cycle]) return '';
      return [
        <div class="opc-cart-item__text text-capitalize opc-cart-item__cycle"> {labels[cycle]}
          {remark ? <span class='text-no-capitalize'>&nbsp;({remark})</span> : ''}
        </div>,
        cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY &&
        (this.$context.is.buyNow || this.$context.is.basic2pro) &&
        <NextChargeDate billing-cycle={cycle} />,
      ];
    },
    // Render discount items using DiscountList component
    mx_discount(planCode) {
      return <PlanDiscountList planCode={planCode} />;
    },
    ma_edit_action(plan) {
      const query = { region: plan?.region }
      // PRO INCREASE - Check Pro Monthly & Yearly Price change before redirect to config plan page
      // Fix the special case: Current sold to is US, then edit to Vietnam without saving, then click edit button in shopping-cart
      this.checkProMonthlyAndYearlyPriceChange()

      this.$router.push({
        path: routePathMap.config,
        query,
      });
      if (this.apmInfo.isApm) {
        this.$nextTick(() => {
          this.setApmInfo(getDefaultPayApmInfo())
        });

      }
      this.$tracking.orderSummary(
        `edit ${this.$pkg.components?.addon ? 'add-on product' : 'product'} - ${this.$t(plan?.planNameKey)}`,
      );
    },
    mx_edit(plan) {
      if (!this.$context.is.checkoutPage || this.$context.is.switchCurrency) return;
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.edit')} ${this.$t(plan?.planNameKey)}`}
          onClick={() => this.ma_edit_action(plan)}
        >
          {this.$t('billing.common.edit')}
        </ZmButton>
      );
    },
    mx_remove_persist_cart(plan) {
      if(!isProductCanRemove(plan) || !this.$context.is.checkoutPage || this.$context.is.switchCurrency) {
        return;
      }
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            this._dispatch('remove', plan);
            this.$tracking.orderSummary(
              `remove ${this.$pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    mx_remove(plan) {
      if(isFromShoppingCart()) {
        return this.mx_remove_persist_cart(plan);
      }
      if (!this.$context.is.checkoutPage || this.$context.is.switchCurrency) return;
      const configType = pageConfig?.editConfig?.planType;
      if(configType === plan.type && plansof(this.getSelectedPlans).type(plan.type).has(1)) return; // base plan can't remove if query contains
      // for campaign link, if only one plan in shopping cart, don't show remove button
      if(
        isFromCampaignLink() &&
        (this.$context?.is?.buyNow || this.$context?.is?.basic2pro) &&
        this.getSelectedPlans?.length <= 1
      ) {
        return null;
      }
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            this._dispatch('remove', plan);
            this.$tracking.orderSummary(
              `remove ${this.$pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
    mx_expire_date(date) {
      if (!date) return '';
      return <div class="opc-cart-item__text first-capitalize"> {date}</div>;
    },
  },
  render() {
    const fn = renderKeys.find((x) => this.$props[x]);
    if (!fn) return null;
    if (!this[`$${fn}`]) return;
    return <div>{this[`$${fn}`]()}</div>;
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-cart-item {
  margin-bottom: 24px;
  @include media(mobile, tablet) {
    margin-bottom: 16px;
  }
  &__value {
    margin-left: auto;
    font-size: 16px;
    font-weight: 500;
    @include media(mobile, tablet) {
      white-space: nowrap;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__title {
    text-align: left;
    font-weight: 700;
    font-size: 18px;
    line-height: 28px;
    color: #00053d;
    display: flex;
    @include media(mobile, tablet) {
      font-size: 14px;
      line-height: 1.5;
    }
  }
  &__sub-title {
    text-align: left;
    font-weight: 700;
    font-size: 14px;
    line-height: 22px;
    color: #00053d;
    display: flex;
    @include media(mobile, tablet) {
      font-weight: 500;
      line-height: 1.5;
    }
  }
  &__text {
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    color: var(--day-foreground-foreground-ada, #666487);
    font-style: normal;

    @include media(mobile, tablet) {
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__duration-text {
    color: #247F40;
  }
  &__discount {
    display: flex;
    color: #247F40;
    font-weight: 450;
    font-size: 16px;
    line-height: 24px;
    justify-content: space-between;
    @include media(mobile, tablet) {
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__discount &__value::before {
    content: '-';
  }
  &--inline &__text {
    display: inline-block;
  }
}
</style>
